import { motion } from "framer-motion";
import { MapPin, Trees, Sprout, Mountain } from "lucide-react";

const STATS = [
  { icon: Trees, label: "Hectáreas totales", value: "8" },
  { icon: MapPin, label: "Lotes ecocampestres", value: "14" },
  { icon: Sprout, label: "m² por lote", value: "3,000" },
  { icon: Mountain, label: "Hectárea de área verde común", value: "1" },
];

export default function AboutCommunity({ content = {} }) {
  return (
    <section
      id="comunidad"
      data-testid="about-section"
      className="relative py-28 bg-gradient-to-b from-[#0B1A0F] via-[#15301E] to-[#0B1A0F] overflow-hidden"
    >
      {/* Decorative blur */}
      <div className="orb w-[480px] h-[480px] bg-[#B8E07A]/40 -top-32 -left-32" />
      <div className="orb w-[380px] h-[380px] bg-[#F1C36B]/30 -bottom-32 -right-32" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-12 items-center">
        {/* Floating image stack — 3D depth */}
        <motion.div
          initial={{ opacity: 0, x: -50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 relative h-[560px]"
          style={{ perspective: "1200px" }}
        >
          <motion.div
            whileHover={{ rotateY: 8, rotateX: -4 }}
            transition={{ type: "spring", stiffness: 100, damping: 15 }}
            className="absolute top-0 left-0 w-[60%] h-[55%] rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)]"
            style={{ transformStyle: "preserve-3d", transform: "translateZ(40px) rotate(-3deg)" }}
            data-testid="about-image-1"
          >
            <img
              src={content.about_image_1 || "https://images.unsplash.com/photo-1500382017468-9049fed747ef?auto=format&fit=crop&w=1200&q=85"}
              alt="Bosque virgen"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1A0F]/40 to-transparent" />
          </motion.div>

          <motion.div
            whileHover={{ rotateY: -8, rotateX: 4 }}
            transition={{ type: "spring", stiffness: 100, damping: 15 }}
            className="absolute top-[30%] right-0 w-[55%] h-[55%] rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.7)] border border-[#F1C36B]/20"
            style={{ transformStyle: "preserve-3d", transform: "translateZ(80px) rotate(4deg)" }}
            data-testid="about-image-2"
          >
            <img
              src={content.about_image_2 || "https://images.pexels.com/photos/6965530/pexels-photo-6965530.jpeg?auto=compress&cs=tinysrgb&w=1200"}
              alt="Arroyo del bosque"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1A0F]/40 to-transparent" />
          </motion.div>

          <motion.div
            whileHover={{ rotateY: 6, rotateX: 6 }}
            transition={{ type: "spring", stiffness: 100, damping: 15 }}
            className="absolute bottom-0 left-[15%] w-[50%] h-[40%] rounded-3xl overflow-hidden shadow-[0_30px_80px_-20px_rgba(0,0,0,0.6)] border border-[#B8E07A]/30"
            style={{ transformStyle: "preserve-3d", transform: "translateZ(60px) rotate(-5deg)" }}
            data-testid="about-image-3"
          >
            <img
              src={content.about_image_3 || "https://images.unsplash.com/photo-1744986924535-ab6ef975f890?auto=format&fit=crop&w=1200&q=85"}
              alt="Montañas verdes"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0B1A0F]/40 to-transparent" />
          </motion.div>
        </motion.div>

        {/* Right text */}
        <motion.div
          initial={{ opacity: 0, x: 50 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-6 text-[#F2EFE6]"
        >
          <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] font-medium text-[#F1C36B]">
            <span className="w-8 h-px bg-[#F1C36B]" /> {content.about_overline || "Sobre Pextlán"}
          </span>

          <h2 className="mt-5 font-serif text-4xl sm:text-5xl leading-[1.05]">
            {content.about_title_1 || "8 hectáreas donde el tiempo se"}{" "}
            <span className="font-fairy text-5xl text-[#B8E07A]">{content.about_title_fairy || "desacelera"}</span>
          </h2>

          <p className="mt-6 text-[#F2EFE6]/80 text-lg leading-relaxed">
            {content.about_description ||
              "Una ecocomunidad campestre en la Sierra de Veracruz, abrazada por bosques de niebla. 14 lotes residenciales de 3,000 m², arroyo propio, mini-lago, y una hectárea entera dedicada a área verde común."}
          </p>

          <div className="mt-8 grid grid-cols-2 gap-4">
            <div
              data-testid="distance-pextlan"
              className="rounded-2xl p-5 bg-[#15301E]/70 border border-[#B8E07A]/15 backdrop-blur"
            >
              <div className="text-[10px] uppercase tracking-widest text-[#F1C36B]">A {content.distance_pextlan || "10 minutos"}</div>
              <div className="mt-1 font-serif text-2xl text-[#F2EFE6]">{content.distance_pextlan_label || "Cañones de Pextlán"}</div>
            </div>
            <div
              data-testid="distance-xico"
              className="rounded-2xl p-5 bg-[#15301E]/70 border border-[#B8E07A]/15 backdrop-blur"
            >
              <div className="text-[10px] uppercase tracking-widest text-[#F1C36B]">A {content.distance_xico || "7 minutos"}</div>
              <div className="mt-1 font-serif text-2xl text-[#F2EFE6]">{content.distance_xico_label || "Pueblo mágico de Xico"}</div>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-5">
            {STATS.map((s) => (
              <div key={s.label} data-testid={`stat-${s.value}`} className="text-center sm:text-left">
                <s.icon className="w-6 h-6 text-[#B8E07A] mx-auto sm:mx-0" />
                <div className="mt-2 font-serif text-3xl text-[#F1C36B]">{s.value}</div>
                <div className="text-[11px] uppercase tracking-wider text-[#F2EFE6]/60 mt-1 leading-snug">
                  {s.label}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 inline-flex items-center gap-3 text-sm text-[#F2EFE6]/75 italic">
            <span className="w-12 h-px bg-[#F1C36B]/60" />
            {content.about_caption || "Sin vallas. Sólo barreras orgánicas que respetan el bosque."}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
